import React from 'react'
import { useRef, useState, useEffect, FC, MouseEvent, CSSProperties } from 'react'
import type { EventContextMenuItemsGetter } from '../../utils/EventActions'

import styles from './style.module.scss'
import moment from 'moment'
import EventCell from '../EventCell'
import * as dateUtils from 'pages/calendar/utils/DateUtils'
import * as calendarService from 'pages/calendar/modules/CalendarService'
import i18n from 'i18n'

const EVENT_CELL_HEIGHT = 20
const EVENT_CELL_MARGIN = 2
const MAX_VISIBLE_ALLDAY_EVENTS = 3

interface Todo {
  status?: string
}

interface CalendarInstance {
  id: string
  subject: string | null
  startDate: Date
  endDate: Date
  allday: boolean
  isBusy: boolean
  isCanceled: boolean
  isDeclined: boolean
  isMeeting: boolean
  myMeetingResponse: string | null
  needsMyResponse: boolean
  isMyEvent: boolean
  color: string
  folderColor: string
  todo?: Todo
}

interface EventCellData {
  style: CSSProperties
  title: string
  key: string
  isOverCount: boolean
  isFree: boolean
  instanceId?: string
  endDate: Date
  date: Date
  allday: boolean
  isNotResponse: boolean
  isMyEvent: boolean
  color: string
  folderColor: string
  isCanceled: boolean
  isDeclined: boolean
  instance?: CalendarInstance
  dayIndex: number
  strechable?: {
    top: boolean
    bottom: boolean
    left: boolean
    right: boolean
  }
  useDragImage?: boolean
  todo?: Todo
  isTodo: boolean
  overCountId?: string
}

interface RemainPosY {
  [key: number]: CalendarInstance
}

interface WeekAlldayProps {
  weekStartDate: Date
  dayLength: number
  instances: CalendarInstance[]
  onEventClick: (event: EventCellData, element: HTMLDivElement | null, dayIndex: number) => void
  onDoubleClick?: (e: MouseEvent<HTMLDivElement>, date: Date) => void
  onEventDoubleClick?: (event: EventCellData, element: HTMLDivElement | null) => void
  getEventContextMenuItems?: EventContextMenuItemsGetter | null
  onChangeStrechArea: (indexes: number[] | null, isLeft: boolean, posY: number) => void
}

const WeekAllday: FC<WeekAlldayProps> = ({
  weekStartDate,
  dayLength,
  instances,
  onEventClick,
  onDoubleClick,
  onEventDoubleClick,
  getEventContextMenuItems,
  onChangeStrechArea,
}) => {
  const thisRef = useRef<HTMLDivElement>(null)
  const [maxVertCount, setMaxVertCount] = useState(0)
  const [alldayEvents, setAlldayEvents] = useState<EventCellData[]>([])

  const weekFirstDate = dateUtils.getDateZero(weekStartDate)
  const weekLastDate = dateUtils.dateAddingDate(weekFirstDate, dayLength)

  const handleEventClick = (event: EventCellData, element: HTMLDivElement | null) => {
    if (onEventClick) {
      onEventClick(event, element, event.dayIndex)
    }
  }

  const handleEventDoubleClick = (event: EventCellData, element: HTMLDivElement | null) => {
    onEventDoubleClick?.(event, element)
  }

  useEffect(() => {
    if (!thisRef.current) {
      return
    }

    let maxCount = 0
    let remainPosY: RemainPosY = {}
    let weekInstances = instances.slice()
    const dayInfoArr: {
      instances: (CalendarInstance | { isEmpty: boolean } | null)[]
      dayDate: Date
      weekFirstDate: Date
      weekLastDate: Date
    }[] = []

    for (let dayIndex = 0; dayIndex < dayLength; dayIndex++) {
      let dayInstances: (CalendarInstance | { isEmpty: boolean } | null)[] = []
      const dayDate = dateUtils.dateAddingDate(weekFirstDate, dayIndex)
      const dayLastDate = dateUtils.dateAddingDate(dayDate, 1)

      for (let instanceIndex = 0; instanceIndex < weekInstances.length; instanceIndex++) {
        const instance = weekInstances[instanceIndex]
        if (instance.startDate.getTime() < dayLastDate.getTime()) {
          while (remainPosY[dayInstances.length]) {
            dayInstances.push({ isEmpty: true })
          }
          dayInstances.push(instance)
          weekInstances.splice(instanceIndex, 1)
          instanceIndex--
        } else {
          break
        }

        if (instance.endDate.getTime() > dayLastDate.getTime()) {
          remainPosY[dayInstances.length - 1] = instance
        }
      }

      const maxIndex = Object.keys(remainPosY).reduce((acc, key) => {
        const index = parseInt(key)
        if (index >= dayInstances.length) {
          acc = index
        }
        return acc
      }, -1)

      for (let i = dayInstances.length; i < maxIndex + 1; i++) {
        if (remainPosY[i]) {
          dayInstances.push({ isEmpty: true })
        } else {
          dayInstances.push(null)
        }
      }

      dayInfoArr.push({
        instances: dayInstances,
        dayDate: dayDate,
        weekFirstDate: weekFirstDate,
        weekLastDate: weekLastDate,
      })

      const tempRemainPosY = remainPosY
      remainPosY = Object.keys(remainPosY).reduce((acc: RemainPosY, key) => {
        const remainInstance = tempRemainPosY[parseInt(key)]
        if (remainInstance.endDate.getTime() > dayLastDate.getTime()) {
          acc[parseInt(key)] = remainInstance
        }
        return acc
      }, {})
    }

    const inEventsCells: EventCellData[] = []
    const rect = thisRef.current.getBoundingClientRect()
    const blockWidth = rect.width / dayLength
    const maxShowCount = MAX_VISIBLE_ALLDAY_EVENTS - 1

    dayInfoArr.forEach((dayInfo, dayIndex) => {
      let overCount = 0
      if (dayInfo.instances.length > maxCount) {
        maxCount = dayInfo.instances.length
        maxCount = maxCount > 3 ? 3 : maxCount
      }

      for (let i = 0; i < dayInfo.instances.length; i++) {
        const instance = dayInfo.instances[i]
        if (i >= maxShowCount) {
          if (instance !== null) {
            overCount++
          }
          continue
        }

        if (instance === null) {
          continue
        }
        if ('isEmpty' in instance && instance.isEmpty) {
          continue
        }

        const calInstance = instance as CalendarInstance

        let allday = calInstance.allday
        let alldayOffset = 0
        if (!allday) {
          const startDate = moment(calInstance.startDate)
          const endDate = moment(calInstance.endDate.getTime() - 1000)
          allday = !startDate.isSame(endDate, 'day')
          alldayOffset = -1000
        } else if (calInstance.startDate.valueOf() !== calInstance.endDate.valueOf()) {
          alldayOffset = -1000
        }

        const strechable = {
          top: false,
          bottom: false,
          left: true,
          right: true,
        }

        let instanceStartDate = calInstance.startDate
        if (instanceStartDate.getTime() < dayInfo.weekFirstDate.getTime()) {
          instanceStartDate = dayInfo.weekFirstDate
          strechable.left = false
        }

        let instanceEndDate = new Date(calInstance.endDate.getTime() + alldayOffset)
        if (instanceEndDate.getTime() >= dayInfo.weekLastDate.getTime()) {
          instanceEndDate = new Date(dayInfo.weekLastDate.getTime() + alldayOffset)
          strechable.right = false
        }

        const dayLen = dateUtils.betweenDay(instanceStartDate, instanceEndDate) + 1

        const topRect = thisRef.current!.getBoundingClientRect()
        let style: CSSProperties = {
          position: 'absolute',
          left: rect.x + 2 - topRect.left + dayIndex * blockWidth,
          top:
            rect.y - topRect.top + EVENT_CELL_MARGIN + i * (EVENT_CELL_HEIGHT + EVENT_CELL_MARGIN),
          width: blockWidth * dayLen - 5,
          height: EVENT_CELL_HEIGHT,
        }

        let title = calInstance.subject
        if (title == null || title.length === 0) {
          title = i18n.t('editor.value.noSubject')
        }
        if (calInstance.todo == null) {
          title = (calInstance.isCanceled ? 'Canceled: ' : '') + title
        }

        const eventCell: EventCellData = {
          style: style,
          title: title,
          key: dayIndex + '_' + calInstance.id,
          isOverCount: false,
          isFree: !calInstance.isBusy,
          instanceId: calInstance.id,
          endDate: calInstance.endDate,
          date: dayInfo.dayDate,
          allday: allday,
          isNotResponse: calInstance.needsMyResponse,
          isMyEvent: calInstance.isMyEvent,
          color: calInstance.color,
          folderColor: calInstance.folderColor,
          isCanceled: calInstance.isCanceled,
          isDeclined: calInstance.isDeclined,
          instance: calInstance,
          dayIndex: dayIndex,
          strechable,
          useDragImage: true,
          todo: calInstance.todo,
          isTodo: calInstance.todo != null,
        }

        inEventsCells.push(eventCell)
      }
      if (overCount > 0) {
        let style: CSSProperties = {
          left: rect.x + 2 + dayIndex * blockWidth,
          top: maxShowCount * (EVENT_CELL_HEIGHT + EVENT_CELL_MARGIN) + rect.y + EVENT_CELL_MARGIN,
          width: blockWidth - 5,
          height: EVENT_CELL_HEIGHT,
          backgroundColor: 'lightgray',
          justifyContent: 'center',
        }
        const eventCell: EventCellData = {
          style: style,
          title: '+' + overCount,
          key: dayIndex + '_' + 'overCount',
          isOverCount: true,
          date: dayInfo.dayDate,
          dayIndex: dayIndex,
          overCountId: 'over-count-' + moment(dayInfo.dayDate).format('YYYY-MM-DD'),
          isFree: false,
          endDate: dayInfo.dayDate,
          allday: true,
          isNotResponse: false,
          isMyEvent: false,
          color: 'lightgray',
          folderColor: 'lightgray',
          isCanceled: false,
          isDeclined: false,
          isTodo: false,
        }
        inEventsCells.push(eventCell)
        console.log('day:%o, overCount: %d', dayInfo.dayDate, overCount)
      }
    })

    setAlldayEvents(inEventsCells)
    setMaxVertCount(maxCount)
  }, [instances])

  const handleDoubleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (onDoubleClick) {
      const rect = thisRef.current!.getBoundingClientRect()
      const x = e.clientX - rect.x

      const colIndex = Math.floor(x / (rect.width / dayLength))
      const date = dateUtils.dateAddingDate(weekStartDate, colIndex)
      onDoubleClick(e, date)
    }
  }

  const strechIndexes = useRef<number[]>([])
  const targetInfo = useRef<{
    instanceId: string | null
    lastIndex: number
    durationDay: number
    posY: number
  }>({
    instanceId: null,
    lastIndex: 0,
    durationDay: 0,
    posY: 0,
  })
  const lastColIndex = useRef<number | null>(null)

  const handleStrechStart = (
    event: EventCellData,
    isTop: boolean,
    isLeft: boolean,
    e: MouseEvent,
  ) => {
    console.log('handleStrechStart', event, isTop, isLeft, e)
    const instance = calendarService.getInstance(event.instanceId!)
    const durationDay = dateUtils.betweenDay(instance.startDate, instance.endDate)
    let lastIndex = 0
    if (isLeft) {
      lastIndex = event.dayIndex + durationDay - 1
    } else {
      lastIndex = event.dayIndex
    }

    const startIndex = dateUtils.betweenDay(weekStartDate, instance.startDate)
    const endIndex = startIndex + durationDay
    const indexes: number[] = []
    for (let i = startIndex; i < endIndex; i++) {
      indexes.push(i)
    }
    strechIndexes.current = indexes

    const el = document.getElementById(event.instanceId!)
    const rect = el!.getBoundingClientRect()
    targetInfo.current = {
      instanceId: event.instanceId!,
      lastIndex: lastIndex,
      durationDay: durationDay,
      posY: rect.y,
    }

    onChangeStrechArea(indexes, isLeft, rect.y)
  }

  const handleStrechMove = (
    event: EventCellData,
    isTop: boolean,
    isLeft: boolean,
    e: globalThis.MouseEvent,
  ) => {
    const rect = thisRef.current!.getBoundingClientRect()
    const x = e.clientX - rect.x

    let lastIndex = targetInfo.current.lastIndex
    const colIndex = Math.floor(x / (rect.width / dayLength))
    if (colIndex === lastColIndex.current) {
      return
    }
    lastColIndex.current = colIndex

    if (
      (isLeft && (colIndex < 0 || colIndex > lastIndex)) ||
      (!isLeft && (colIndex > dayLength - 1 || colIndex < lastIndex))
    ) {
      return
    }

    console.log('handleStrechMove', isLeft, e)

    const indexes: number[] = []
    if (isLeft) {
      if (lastIndex < colIndex) {
        lastIndex = colIndex
      }
      for (let i = colIndex; i <= lastIndex; i++) {
        indexes.push(i)
      }
    } else {
      if (lastIndex > colIndex) {
        lastIndex = colIndex
      }
      for (let i = lastIndex; i <= colIndex; i++) {
        indexes.push(i)
      }
    }
    console.log('indexes', indexes, colIndex, lastIndex)
    strechIndexes.current = indexes
    onChangeStrechArea(indexes, isLeft, targetInfo.current.posY)
  }

  const handleStrechEnd = (
    event: EventCellData,
    isTop: boolean,
    isLeft: boolean,
    e: globalThis.MouseEvent | KeyboardEvent,
    isCancel: boolean,
  ) => {
    console.log('handleStrechEnd', event, isTop, isLeft, e, isCancel)

    if (isCancel) {
      strechIndexes.current = []
      onChangeStrechArea(null, isLeft, 0)
      return
    }

    const rect = thisRef.current!.getBoundingClientRect()
    const mouseEvent = e as globalThis.MouseEvent
    const x = mouseEvent.clientX - rect.x
    const y = mouseEvent.clientY - rect.y
    if (x < 0 || x > rect.width || y < -40 || y > rect.height) {
      strechIndexes.current = []
      onChangeStrechArea(null, isLeft, 0)
      return
    }

    const indexes = strechIndexes.current
    if (indexes.length === 0) {
      strechIndexes.current = []
      onChangeStrechArea(null, isLeft, 0)
      return
    }

    const instance = calendarService.getInstance(event.instanceId!)
    if (instance === null) {
      console.error('instance not found')
      strechIndexes.current = []
      onChangeStrechArea(null, isLeft, 0)
      return
    }

    const d = new Date(weekStartDate)
    d.setHours(0, 0, 0, 0)
    const startIndex = indexes[0]
    const startDate = dateUtils.dateAddingDate(d, startIndex)
    const endDate = dateUtils.dateAddingDate(d, startIndex + indexes.length + 1)
    calendarService.updateEventChangeTime(instance, startDate, endDate, true)

    strechIndexes.current = []
    onChangeStrechArea(null, isLeft, 0)
  }

  return (
    <div
      className={styles.weekAlldayContainer}
      ref={thisRef}
      style={
        {
          '--height': `${maxVertCount * (EVENT_CELL_HEIGHT + EVENT_CELL_MARGIN) + (maxVertCount > 0 ? EVENT_CELL_MARGIN : 0)}px`,
        } as CSSProperties
      }
      onDoubleClick={handleDoubleClick}
    >
      <div className={styles.separators} style={{ '--day-length': dayLength } as CSSProperties}>
        {Array.from({ length: dayLength }).map((_, index) => (
          <div key={`separator-${index}`} className={styles.separator} />
        ))}
      </div>

      <div
        className={styles.eventsContainer}
        style={{ '--day-length': dayLength } as CSSProperties}
      >
        {alldayEvents.map((event) => (
          <EventCell
            key={event.key}
            {...({
              overCountId: event.overCountId,
              event: event,
              onCellClick: handleEventClick,
              onCellDoubleClick: handleEventDoubleClick,
              getContextMenuItems: getEventContextMenuItems,
              forDay: false,
              onStrechStart: handleStrechStart,
              onStrechMove: handleStrechMove,
              onStrechEnd: handleStrechEnd,
            } as any)}
          />
        ))}
      </div>
    </div>
  )
}

export default WeekAllday
